import { useState, useRef, useCallback } from 'react';
import { apiJson, buildApiUrl } from '../utils/apiClient';
import { logger } from "../utils/logger";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let SpeechSDK: any = null;

async function loadSpeechSDK() {
  if (!SpeechSDK) {
    SpeechSDK = await import('microsoft-cognitiveservices-speech-sdk');
  }
  return SpeechSDK;
}

interface SpeechTokenResponse {
  token: string;
  region: string;
}

async function fetchSpeechToken(): Promise<SpeechTokenResponse> {
  const url = buildApiUrl('/voice/speech-token');
  return apiJson(url);
}

const TARGET_SAMPLE_RATE = 16000;
const TOKEN_REFRESH_INTERVAL_MS = 9 * 60 * 1000; // Refresh every 9 minutes (tokens expire at 10)
const WEB_SPEECH_MAX_RESTARTS = 10;
const WEB_SPEECH_RESTART_DELAY_MS = 500;

function resampleAndConvert(float32: Float32Array, sourceSampleRate: number): Int16Array {
  let samples = float32;
  if (sourceSampleRate !== TARGET_SAMPLE_RATE) {
    const ratio = sourceSampleRate / TARGET_SAMPLE_RATE;
    const outLength = Math.round(float32.length / ratio);
    samples = new Float32Array(outLength);
    for (let i = 0; i < outLength; i++) {
      const srcIdx = i * ratio;
      const lo = Math.floor(srcIdx);
      const hi = Math.min(lo + 1, float32.length - 1);
      const frac = srcIdx - lo;
      samples[i] = float32[lo] * (1 - frac) + float32[hi] * frac;
    }
  }
  const pcm16 = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return pcm16;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function registerWorklet(audioContext: any) {
  try {
    await audioContext.audioWorklet.addModule('/speech-processor.js');
    logger.log('[AzureSpeech] AudioWorklet registered via static file');
    return;
  } catch (staticErr) {
    logger.warn('[AzureSpeech] Static file addModule failed:', (staticErr as Error).message);
  }
  try {
    const resp = await window.fetch('/speech-processor.js');
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const code = await resp.text();
    if (code.includes('<!DOCTYPE') || code.includes('<html')) {
      throw new Error('Server returned HTML instead of JS (SPA catch-all)');
    }
    const blob = new Blob([code], { type: 'application/javascript' });
    const blobUrl = URL.createObjectURL(blob);
    await audioContext.audioWorklet.addModule(blobUrl);
    URL.revokeObjectURL(blobUrl);
    logger.log('[AzureSpeech] AudioWorklet registered via blob URL fallback');
    return;
  } catch (blobErr) {
    logger.warn('[AzureSpeech] Blob URL addModule also failed:', (blobErr as Error).message);
  }
  throw new Error('AudioWorklet registration failed — browser may not support AudioWorklet');
}

// ---------------------------------------------------------------------------
// Web Speech API helpers (browser-native STT fallback)
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SpeechRecognitionCtor = new () => any;

function getWebSpeechRecognition(): SpeechRecognitionCtor | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export type AzureSpeechError =
  | 'web_speech_unsupported'
  | 'web_speech_error'
  | 'web_speech_start_failed'
  | 'recognition_error';

export default function useAzureSpeech() {
  const [interimText, setInterimText] = useState<string>('');
  const [finalSegments, setFinalSegments] = useState<string[]>([]);
  const [isRecognizing, setIsRecognizing] = useState<boolean>(false);
  const [error, setError] = useState<AzureSpeechError | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognizerRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const audioConfigRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pushStreamRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const audioContextRef = useRef<any>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const finalSegmentsRef = useRef<string[]>([]);
  // Tracks which STT backend is active so stop/cleanup behave correctly
  const backendRef = useRef<'azure' | 'web' | null>(null);
  // Token refresh timer
  const tokenRefreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Web Speech restart counter for backoff
  const webSpeechRestartCountRef = useRef<number>(0);

  const releaseAudioResources = useCallback(() => {
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch (_) { void _; }
      audioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
    if (pushStreamRef.current) {
      try { pushStreamRef.current.close(); } catch (_) { void _; }
      pushStreamRef.current = null;
    }
    if (tokenRefreshTimerRef.current) {
      clearInterval(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }
  }, []);

  // -----------------------------------------------------------------------
  // Web Speech API fallback — starts browser-native continuous recognition
  // -----------------------------------------------------------------------
  const startWebSpeech = useCallback((): boolean => {
    const Ctor = getWebSpeechRecognition();
    if (!Ctor) {
      logger.error('[WebSpeech] Browser does not support SpeechRecognition');
      setError('web_speech_unsupported');
      return false;
    }

    const recognition = new Ctor();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    // Reset restart counter on fresh start
    webSpeechRestartCountRef.current = 0;

    recognition.onresult = (event: { resultIndex: number; results: { length: number; [i: number]: { isFinal: boolean; 0: { transcript: string } }[] } }) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = (event.results as any)[i];
        const transcript: string = result[0].transcript;
        if (result.isFinal) {
          logger.log('[WebSpeech] final segment:', transcript.slice(0, 60));
          finalSegmentsRef.current = [...finalSegmentsRef.current, transcript];
          setFinalSegments([...finalSegmentsRef.current]);
          // Reset restart counter on successful recognition
          webSpeechRestartCountRef.current = 0;
        } else {
          interim += transcript;
        }
      }
      setInterimText(interim);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onerror = (event: any) => {
      // 'no-speech' and 'aborted' are expected during normal usage
      if (event.error === 'no-speech' || event.error === 'aborted') {
        logger.log('[WebSpeech] non-fatal error:', event.error);
        return;
      }
      logger.error('[WebSpeech] error:', event.error);
      setError('web_speech_error');
    };

    // Auto-restart on end with backoff and max retry limit
    recognition.onend = () => {
      if (backendRef.current === 'web') {
        webSpeechRestartCountRef.current += 1;
        if (webSpeechRestartCountRef.current > WEB_SPEECH_MAX_RESTARTS) {
          logger.warn('[WebSpeech] Max restarts reached, stopping');
          setError('web_speech_error');
          setIsRecognizing(false);
          backendRef.current = null;
          return;
        }
        const delay = WEB_SPEECH_RESTART_DELAY_MS * webSpeechRestartCountRef.current;
        logger.log(`[WebSpeech] restarting after auto-stop (attempt ${webSpeechRestartCountRef.current}, delay ${delay}ms)`);
        setTimeout(() => {
          if (backendRef.current === 'web') {
            try { recognition.start(); } catch (_) { void _; }
          }
        }, delay);
      }
    };

    try {
      recognition.start();
      logger.log('[WebSpeech] recognition started (browser fallback)');
    } catch (e) {
      logger.error('[WebSpeech] start failed:', e);
      setError('web_speech_start_failed');
      return false;
    }

    recognizerRef.current = recognition;
    backendRef.current = 'web';
    setIsRecognizing(true);
    return true;
  }, []);

  // -----------------------------------------------------------------------
  // Azure Speech SDK — primary STT path
  // -----------------------------------------------------------------------
  const startAzure = useCallback(async (): Promise<boolean> => {
    let sdk;
    try {
      sdk = await loadSpeechSDK();
    } catch (e) {
      logger.error('[AzureSpeech] Failed to load SDK:', e);
      return false;
    }

    let tokenData: SpeechTokenResponse;
    try {
      tokenData = await fetchSpeechToken();
      logger.log('[AzureSpeech] Token fetched, region:', tokenData.region);
    } catch (e) {
      logger.error('[AzureSpeech] Failed to fetch token:', e);
      return false;
    }

    const mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
    });
    mediaStreamRef.current = mediaStream;
    const tracks = mediaStream.getAudioTracks();
    logger.log('[AzureSpeech] Mic acquired, tracks:', tracks.length, 'label:', tracks[0]?.label);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    const audioContext = new AudioCtx({ sampleRate: 16000 });
    audioContextRef.current = audioContext;
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    logger.log('[AzureSpeech] AudioContext state:', audioContext.state, 'sampleRate:', audioContext.sampleRate);

    await registerWorklet(audioContext);

    const ctxRate = audioContext.sampleRate;
    const needsResample = ctxRate !== TARGET_SAMPLE_RATE;
    logger.log('[AzureSpeech] Context sampleRate:', ctxRate, needsResample ? '(will resample to 16kHz)' : '(native 16kHz)');

    const pushStream = sdk.AudioInputStream.createPushStream(
      sdk.AudioStreamFormat.getWaveFormatPCM(TARGET_SAMPLE_RATE, 16, 1),
    );
    pushStreamRef.current = pushStream;

    const source = audioContext.createMediaStreamSource(mediaStream);
    const workletNode = new AudioWorkletNode(audioContext, 'speech-processor');
    workletNode.port.onmessage = (event: MessageEvent) => {
      const float32 = event.data as Float32Array;
      if (float32 && pushStreamRef.current) {
        pushStream.write(resampleAndConvert(float32, ctxRate).buffer);
      }
    };
    source.connect(workletNode);
    workletNode.connect(audioContext.destination);
    logger.log('[AzureSpeech] Pipeline: mic -> AudioWorkletNode -> PushStream');

    const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(
      tokenData.token,
      tokenData.region,
    );
    speechConfig.speechRecognitionLanguage = 'en-US';

    const audioConfig = sdk.AudioConfig.fromStreamInput(pushStream);
    audioConfigRef.current = audioConfig;

    const recognizer = new sdk.SpeechRecognizer(speechConfig, audioConfig);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognizer.sessionStarted = (_sender: any, event: any) => {
      logger.log('[AzureSpeech] sessionStarted, id:', event.sessionId);
    };

    recognizer.speechStartDetected = () => {
      logger.log('[AzureSpeech] speechStartDetected — audio reaching Azure');
    };

    recognizer.speechEndDetected = () => {
      logger.log('[AzureSpeech] speechEndDetected');
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognizer.recognizing = (_sender: any, event: any) => {
      if (event.result.text) {
        logger.log('[AzureSpeech] interim:', event.result.text.slice(0, 60));
        setInterimText(event.result.text);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognizer.recognized = (_sender: any, event: any) => {
      if (event.result.reason === sdk.ResultReason.RecognizedSpeech && event.result.text) {
        logger.log('[AzureSpeech] final segment:', event.result.text.slice(0, 60));
        finalSegmentsRef.current = [...finalSegmentsRef.current, event.result.text];
        setFinalSegments(finalSegmentsRef.current);
        setInterimText('');
      } else {
        logger.log('[AzureSpeech] recognized but no text, reason:', event.result.reason);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognizer.canceled = (_sender: any, event: any) => {
      const reasonName = event.reason === sdk.CancellationReason.Error ? 'Error'
        : event.reason === sdk.CancellationReason.EndOfStream ? 'EndOfStream'
        : `Unknown(${event.reason})`;
      logger.warn('[AzureSpeech] canceled reason:', reasonName, 'details:', event.errorDetails);
      if (event.reason === sdk.CancellationReason.Error) {
        logger.error('[AzureSpeech] ErrorCode:', event.errorCode, 'Details:', event.errorDetails);
        setError('recognition_error');
      }
    };

    recognizer.sessionStopped = () => {
      logger.log('[AzureSpeech] sessionStopped');
      setIsRecognizing(false);
    };

    recognizerRef.current = recognizer;
    backendRef.current = 'azure';

    await new Promise<void>((resolve, reject) => {
      recognizer.startContinuousRecognitionAsync(
        () => {
          logger.log('[AzureSpeech] recognition started — speak now');
          resolve();
        },
        (err: string) => {
          logger.error('[AzureSpeech] start failed:', err);
          reject(err);
        },
      );
    });

    // Clear any lingering token refresh timer from a previous session
    if (tokenRefreshTimerRef.current) {
      clearInterval(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }

    // Set up token refresh timer — Azure tokens expire after 10 minutes
    tokenRefreshTimerRef.current = setInterval(async () => {
      try {
        const newTokenData = await fetchSpeechToken();
        if (recognizerRef.current && backendRef.current === 'azure') {
          recognizerRef.current.authorizationToken = newTokenData.token;
          logger.log('[AzureSpeech] Token refreshed successfully');
        }
      } catch (e) {
        logger.warn('[AzureSpeech] Token refresh failed (will retry next interval):', (e as Error).message);
      }
    }, TOKEN_REFRESH_INTERVAL_MS);

    setIsRecognizing(true);
    return true;
  }, []);

  // -----------------------------------------------------------------------
  // start — tries Azure SDK first, falls back to Web Speech API
  // -----------------------------------------------------------------------
  const start = useCallback(async () => {
    setError(null);
    setInterimText('');
    setFinalSegments([]);
    finalSegmentsRef.current = [];

    // Clean up any prior session resources before starting fresh
    releaseAudioResources();
    if (audioConfigRef.current) {
      try { audioConfigRef.current.close(); } catch (_) { void _; }
      audioConfigRef.current = null;
    }
    if (recognizerRef.current) {
      const staleRecognizer = recognizerRef.current;
      recognizerRef.current = null;
      // Detach callbacks before closing to prevent stale error state on new session
      try { staleRecognizer.canceled = undefined; } catch (_) { void _; }
      try { staleRecognizer.close(); } catch (_) { void _; }
    }
    backendRef.current = null;

    // Try Azure Speech SDK first
    try {
      const azureOk = await startAzure();
      if (azureOk) return true;
    } catch (e) {
      logger.warn('[AzureSpeech] Azure STT setup failed, trying Web Speech fallback:', (e as Error).message);
      releaseAudioResources();
    }

    // Fall back to browser Web Speech API
    logger.log('[AzureSpeech] Falling back to browser Web Speech API for STT');
    return startWebSpeech();
  }, [startAzure, startWebSpeech, releaseAudioResources]);

  const stop = useCallback(() => {
    return new Promise<string[]>((resolve) => {
      const recognizer = recognizerRef.current;
      const backend = backendRef.current;

      if (!recognizer) {
        releaseAudioResources();
        setIsRecognizing(false);
        backendRef.current = null;
        resolve(finalSegmentsRef.current);
        return;
      }

      if (backend === 'web') {
        // Web Speech API — just call stop()
        try { recognizer.stop(); } catch (_) { void _; }
        recognizerRef.current = null;
        backendRef.current = null;
        releaseAudioResources();
        setIsRecognizing(false);
        setInterimText('');
        resolve(finalSegmentsRef.current);
        return;
      }

      // Azure SDK — async stop
      recognizer.stopContinuousRecognitionAsync(
        () => {
          logger.log('[AzureSpeech] recognition stopped');
          recognizer.close();
          recognizerRef.current = null;
          backendRef.current = null;
          if (audioConfigRef.current) {
            try { audioConfigRef.current.close(); } catch (_) { void _; }
            audioConfigRef.current = null;
          }
          releaseAudioResources();
          setIsRecognizing(false);
          setInterimText('');
          resolve(finalSegmentsRef.current);
        },
        (err: string) => {
          logger.error('[AzureSpeech] stop error:', err);
          recognizerRef.current = null;
          backendRef.current = null;
          if (audioConfigRef.current) {
            try { audioConfigRef.current.close(); } catch (_) { void _; }
            audioConfigRef.current = null;
          }
          releaseAudioResources();
          setIsRecognizing(false);
          setInterimText('');
          resolve(finalSegmentsRef.current);
        },
      );
    });
  }, [releaseAudioResources]);

  // Clear accumulated segments without stopping the mic session.
  // Returns the authoritative ref value before clearing (avoids React state lag).
  const resetSegments = useCallback((): string[] => {
    const current = finalSegmentsRef.current;
    setInterimText('');
    setFinalSegments([]);
    finalSegmentsRef.current = [];
    return current;
  }, []);

  const cleanup = useCallback(() => {
    const recognizer = recognizerRef.current;
    const backend = backendRef.current;

    if (recognizer) {
      if (backend === 'web') {
        try { recognizer.stop(); } catch (_) { void _; }
      } else {
        try {
          recognizer.stopContinuousRecognitionAsync(
            () => { recognizer.close(); },
            () => { recognizer.close(); },
          );
        } catch (_e) {
          void _e;
        }
      }
      recognizerRef.current = null;
    }
    backendRef.current = null;
    if (audioConfigRef.current) {
      try { audioConfigRef.current.close(); } catch (_) { void _; }
      audioConfigRef.current = null;
    }
    releaseAudioResources();
    setIsRecognizing(false);
    setInterimText('');
  }, [releaseAudioResources]);

  return {
    interimText,
    finalSegments,
    isRecognizing,
    error,
    start,
    stop,
    resetSegments,
    cleanup,
  };
}

export type AzureSpeechReturn = ReturnType<typeof useAzureSpeech>;
